<template>
  <div class="member-delegates-wrap">
    <!-- ── Summary Stats ────────────────────── -->
    <div class="stats-row mb-8">
      <div class="stat-card-container">
        <PremiumStatCard
          label="Active Delegates"
          :value="delegates.length"
          icon="mdi-account-group-outline"
          color="#8B5CF6"
          :loading="loading"
          :progress="100"
        />
      </div>
    </div>

    <!-- ── Delegates Table ──────────────────── -->
    <div v-if="delegates.length > 0 || loading">
      <PremiumTable
        title="Delegates"
        :columns="delegateColumns"
        :items="delegates"
        :loading="loading"
        :searchable="false"
        :show-footer="false"
      >
        <!-- Identity Column -->
        <template #col-delegateMember="{ item }">
          <div class="del-avatar-wrap">
            <img
              v-if="item.delegateMember && item.delegateMember.profile_url"
              :src="
                API_BASE_URL + '/uploads/' + item.delegateMember.profile_url
              "
              class="del-img"
            />
            <div
              v-else
              class="del-placeholder"
              :style="{ background: primaryColor + '15', color: primaryColor }"
            >
              {{
                item.delegateMember && item.delegateMember.name
                  ? item.delegateMember.name.charAt(0).toUpperCase()
                  : "D"
              }}
            </div>
            <div class="del-identity">
              <span class="del-name">{{
                item.delegateMember ? item.delegateMember.name : "Unknown"
              }}</span>
              <span class="del-phone">{{
                item.delegateMember
                  ? item.delegateMember.country_code +
                    " " +
                    item.delegateMember.mobile_no
                  : ""
              }}</span>
            </div>
          </div>
        </template>

        <!-- Type Column -->
        <template #col-delegate_type="{ item }">
          <span class="del-type-text">{{ formatLabel(item.delegate_type) }}</span>
        </template>

        <!-- Permissions Column -->
        <template #col-permissions="{ item }">
          <div class="permissions-cell">
            <v-menu
              v-if="item.permissions && item.permissions.length > 0"
              open-on-hover
              bottom
              offset-y
              :close-delay="200"
              max-width="320"
              content-class="premium-menu-tooltip"
            >
              <template v-slot:activator="{ on, attrs }">
                <div class="permissions-tags" v-on="on" v-bind="attrs" title="">
                  <div
                    v-for="(mod, i) in item.permissions.slice(0, 3)"
                    :key="i"
                    class="permission-tag"
                  >
                    {{ formatLabel(mod) }}
                  </div>
                  <div v-if="item.permissions.length > 3" class="permission-tag more-tag">
                    +{{ item.permissions.length - 3 }} More
                  </div>
                </div>
              </template>
              <div class="premium-tooltip-card">
                <div class="tooltip-title">Granted Permissions</div>
                <div class="premium-tooltip-scroll">
                  <div class="tooltip-grid">
                    <div v-for="(mod, i) in item.permissions" :key="i" class="tooltip-tag">
                      {{ formatLabel(mod) }}
                    </div>
                  </div>
                </div>
              </div>
            </v-menu>
            <span
              v-else
              class="text-caption grey--text italic"
            >
              No Access Granted
            </span>
          </div>
        </template>

        <!-- Status Column -->
        <template #col-status="{ item }">
          <div :class="['premium-status-chip', item.status]">
            <span class="status-dot"></span>
            {{ formatLabel(item.status) }}
          </div>
        </template>

        <!-- Actions Column -->
        <template #col-actions="{ item }">
          <v-btn
            icon
            small
            color="red lighten-1"
            @click="confirmRemove(item)"
            title="Remove Delegate"
          >
            <v-icon size="18">mdi-trash-can-outline</v-icon>
          </v-btn>
        </template>
      </PremiumTable>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state-premium">
      <div class="empty-icon-box">
        <v-icon size="40" color="#8B5CF6">mdi-account-off-outline</v-icon>
      </div>
      <h4>No Delegates Found</h4>
      <p>
        This member unit hasn't assigned any family members or tenants as
        delegates yet.
      </p>
    </div>

    <!-- ── Confirm Remove Modal ─────────────── -->
    <ConfirmModal
      v-model="removeDialog"
      type="danger"
      title="Remove Delegate"
      message="Are you sure you want to remove this delegate?"
      confirm-text="Yes, Remove"
      :loading="removing"
      @confirm="removeDelegate"
    />
  </div>
</template>

<script>
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumStatCard from "../../common/PremiumStatCard.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import { mapMutations, mapState } from "vuex";

export default {
  name: "MemberDelegates",

  components: { PremiumTable, PremiumStatCard, ConfirmModal },

  props: {
    member_id: { type: [String, Number], required: true },
    unit_mapping_id: { type: [String, Number], required: false },
  },

  data() {
    return {
      loading: false,
      removing: false,
      removeDialog: false,
      selectedDelegate: null,
      delegates: [],
      delegateColumns: [
        { key: "delegateMember", label: "Delegate Details", width: "300px" },
        { key: "delegate_type", label: "Type", width: "100px" },
        { key: "permissions", label: "Permissions"},
        { key: "status", label: "Status", width: "110px" },
        { key: "actions", label: "Actions", width: "80px", sortable: false },
      ],
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,
  },

  watch: {
    unit_mapping_id: {
      immediate: true,
      handler(val) {
        if (val) this.fetchDelegates();
      },
    },
  },

  mounted() {
    this.fetchDelegates();
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    async fetchDelegates() {
      let mid = this.unit_mapping_id || this.$route.query.unit_mapping_id;

      this.loading = true;
      try {
        // If no unit_mapping_id is provided, try to fetch it from the member details
        if (!mid) {
          const detailRes = await this.$axios.get(
            `societyadmin/members/${this.member_id}/details`,
          );
          if (detailRes.data.status && detailRes.data.data.unit_mapping_id) {
            mid = detailRes.data.data.unit_mapping_id;
          }
        }

        if (!mid) {
          this.delegates = [];
          return;
        }

        const response = await this.$axios.get(
          `societyadmin/units/${mid}/delegates`,
        );
        if (response.data.status) {
          this.delegates = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching delegates:", error);
      } finally {
        this.loading = false;
      }
    },

    confirmRemove(item) {
      this.selectedDelegate = item;
      this.removeDialog = true;
    },

    async removeDelegate() {
      if (!this.selectedDelegate) return;
      this.removing = true;
      try {
        const response = await this.$axios.delete(
          `societyadmin/delegates/${this.selectedDelegate.id}`,
        );
        if (response.data.status) {
          this.setAlert({
            showAlert: true,
            variant: "green",
            timeout: "2000",
            message: "Delegate removed successfully",
          });
          this.removeDialog = false;
          this.fetchDelegates();
        } else {
          this.showError(response.data.message);
        }
      } catch (error) {
        this.showError("Something went wrong while removing delegate");
      } finally {
        this.removing = false;
      }
    },

    formatLabel(val) {
      if (!val) return "";
      return val
        .toString()
        .replace(/_/g, " ")
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red",
        timeout: "3000",
        message: msg,
      });
    },
  },
};
</script>

<style scoped>
.stats-row {
  display: flex;
}
.stat-card-container {
  width: 320px;
}

/* ── Avatar & Identity ── */
.del-avatar-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.del-img {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  object-fit: contain;
  border: 2px solid var(--border);
}
.del-placeholder {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
}
.del-identity {
  display: flex;
  flex-direction: column;
}
.del-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
}
.del-phone {
  font-size: 12px;
  color: var(--tx3);
  font-weight: 500;
}

.permissions-tags {
  display: flex;
  gap: 6px;
  cursor: help;
}
.permission-tag {
  background: transparent;
  color: var(--brand);
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid var(--brand);
  text-transform: capitalize;
  transition: all 0.2s;
  white-space: nowrap;
}
.more-tag {
  background: rgba(var(--brand), 0.08) !important;
  color: var(--brand) !important;
  border-color: rgba(var(--brand), 0.1) !important;
}

/* ── Tooltip ── */
.premium-tooltip-card {
  background: var(--brand, #30384d);
  color: #fff;
  padding: 14px 16px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.premium-tooltip-scroll {
  max-height: 250px;
  overflow-y: auto;
  padding-right: 4px;
}
.tooltip-title {
  font-size: 10px;
  font-weight: 800;
  color: rgba(var(--brand));
  text-transform: uppercase;
  margin-bottom: 12px;
  letter-spacing: 0.08em;
  padding-left: 2px;
}
.tooltip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tooltip-tag {
  background: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* Custom scrollbar for tooltips */
.premium-tooltip-scroll::-webkit-scrollbar {
  width: 5px;
}
.premium-tooltip-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}
.premium-tooltip-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 10px;
}

/* ── Type Text ── */
.del-type-text {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx2);
}

/* ── Status Chip ── */
.premium-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.premium-status-chip.active {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}
.premium-status-chip.inactive {
  background: rgba(100, 116, 139, 0.1);
  color: #64748b;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

/* ── Empty State ── */
.empty-state-premium {
  background: var(--surface2);
  border: 1px dashed var(--border);
  border-radius: 16px;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}
.empty-icon-box {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(139, 92, 246, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}
.empty-state-premium h4 {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: var(--tx);
  margin: 0;
}
.empty-state-premium p {
  font-size: 14px;
  color: var(--tx3);
  margin: 0;
}
</style>
